import {
  useCompany,
  useFnolRequest,
  useGetCompanyAttributeOptions,
  useGetResourcesPaginated,
  useIngressRequest,
} from '@dispatch/shared/hooks';
import {
  FNOL_AvailabilityResponse,
  FNOL_ClaimOwnerOrComplexAvailabilityResponse,
  TaskCreate_FormSections,
  MultiSelectOption,
  FNOL_RequestForm,
  AvailableSlots,
  FNOL_Request,
  IngressRequestForm,
  IngressRequest,
  FNOL_TaskTypes,
} from '@dispatch/shared/types';
import { TaskCreate } from '@dispatch/web/features/tasks';
import { useNotification } from '@dispatch/web/hooks';
import { zonedTimeToUtc } from 'date-fns-tz';
import { isNull } from 'lodash';
import { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { v4 as uuidv4 } from 'uuid';

export function TaskCreatePage() {
  const [formSection, setFormSection] = useState<TaskCreate_FormSections>(
    TaskCreate_FormSections.INITIAL
  );
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [isForced, setIsForced] = useState<boolean>(false);
  const [availableSlots, setAvailableSlots] = useState<
    AvailableSlots[] | FNOL_ClaimOwnerOrComplexAvailabilityResponse
  >([]);
  const [saveButtonLabel, setSaveButtonLabel] = useState('Next');

  const { notifyError, notifySuccess } = useNotification();
  const [resourceInput, setResourceInput] = useState<string>('');
  const [resourceDebouncedValue, setResourceDebouncedValue] =
    useState<string>('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [timeZone, setTimeZone] = useState<string>('');

  useDebounce(
    () => {
      setResourceDebouncedValue(resourceInput);
    },
    1000,
    [resourceInput]
  );

  const activeResources = useGetResourcesPaginated({
    filters: {
      resourceName: resourceDebouncedValue,
      isActive: true,
      pageSize: 25, //search only first 25
    },
    enabled: !!resourceDebouncedValue,
  });

  const mappedActiveResources = useMemo(() => {
    if (activeResources.status === 'success' && activeResources.data) {
      return activeResources.data?.map((resource): MultiSelectOption => {
        return {
          title: resource.name,
          value: resource.integrationGUID,
        };
      });
    }
  }, [activeResources.data, activeResources.status]);

  const setAvailableSlotsHelper = (data: FNOL_AvailabilityResponse) => {
    if (data.availableResourcesSlots) {
      setAvailableSlots(data.availableResourcesSlots || []);
    } else {
      setAvailableSlots(data.availableSlots || []);
    }
  };

  const onFnolRequest = (response: {
    data:
      | FNOL_AvailabilityResponse
      | FNOL_ClaimOwnerOrComplexAvailabilityResponse;
    success: boolean;
  }) => {
    if (response.success) {
      if (
        (response.data as FNOL_ClaimOwnerOrComplexAvailabilityResponse)
          .projectedWorkStartTime
      ) {
        setAvailableSlots(
          response.data as FNOL_ClaimOwnerOrComplexAvailabilityResponse
        );
      } else {
        setAvailableSlotsHelper(response.data as FNOL_AvailabilityResponse);
      }
      setFormSection(TaskCreate_FormSections.AVAILABLE_SLOTS);
    } else {
      notifyError('Error', 'FNOL request failed');
    }
  };

  const { fnolRequest, status: fnolStatus } = useFnolRequest(onFnolRequest);

  const fnolRequestHelper = (values: FNOL_RequestForm): FNOL_Request => {
    return {
      lossLocation: values.lossLocation,
      methodOfInspection: values.methodOfInspection,
      catCode: values.catCode,
      severity: values.severity,
      peril: values.peril,
      startTime: values.startTime.toISOString(),
      endTime: values.endTime.toISOString(),
      durationInMinutes: isNull(values.durationInMinutes)
        ? 0
        : values.durationInMinutes,
      preferredLanguage: values.preferredLanguage,
      assignmentCategory: values.assignmentCategory,
      onSunday: values.onSunday,
      onMonday: values.onMonday,
      onTuesday: values.onTuesday,
      onWednesday: values.onWednesday,
      onThursday: values.onThursday,
      onFriday: values.onFriday,
      onSaturday: values.onSaturday,
      requiredAttributes:
        values.claimAttributes.length > 0
          ? Object.assign(
              {},
              ...values.claimAttributes.map((value) => ({
                [value.attributeDefinitionIntegrationGUID]:
                  value.selectedAttributeValuesVarchars,
              }))
            )
          : undefined,
      resourceIds: values.resourceIds
        ? values.resourceIds.map((value) => {
            return value.value as string;
          })
        : undefined,
    };
  };

  const onHandleFNOLRequestFormSubmit = (values: FNOL_RequestForm) => {
    // This timezone allows us to display the times in the user selected timezone
    // In the future this timezone will be grabbed via geoResolver and passed back in the fnol response
    setTimeZone(values.timeZone);
    const request = fnolRequestHelper(values);

    fnolRequest({ request: request, taskType: values.fnolTaskType });
  };

  const ingressRequestHelper = (values: IngressRequestForm): IngressRequest => {
    let startTime: string, endTime: string;

    if (isForced) {
      startTime = zonedTimeToUtc(
        values.assignmentHandling.mustStartAfter,
        values.timeZone || ''
      ).toISOString();
      endTime = zonedTimeToUtc(
        values.assignmentHandling.mustEndBefore,
        values.timeZone || ''
      ).toISOString();
    } else {
      if (values.assignmentHandling.taskType === FNOL_TaskTypes.NON_COMPLEX) {
        const _selectedSlot = (availableSlots as AvailableSlots[])[
          selectedSlot || 0
        ];

        startTime = _selectedSlot.projectedWorkStartTime;
        endTime = _selectedSlot.projectedWorkEndTime;
      } else {
        const _selectedSlot =
          availableSlots as FNOL_ClaimOwnerOrComplexAvailabilityResponse;

        startTime = _selectedSlot.projectedWorkStartTime;
        endTime = startTime;
      }
    }

    return {
      $msg: {
        externalId: uuidv4(),
        claim: {
          lossLocation: values.claim.lossLocation,
          jobId: values.claim.jobId,
          taskId: values.claim.taskId,
          claimNumber: values.claim.claimNumber,
          insuredName: values.claim.insuredName,
          catCode: values.claim.catCode,
          propertyType: values.claim.propertyType,
          dateOfLoss: values.claim.dateOfLoss,
          dateOfNotice: values?.claim?.dateOfNotice,
          peril: values.claim.peril,
          sourcedFrom: values.claim.sourcedFrom,
          isModularHome: values.claim.isModularHome,
          jobNumber: values.claim.jobNumber,
        },
        contact: {
          primary: values.contact.primary,
          secondary: values.contact.hasSecondaryContact
            ? values.contact.secondary
            : undefined,
          altLocation: values.contact.hasAlternateLocation
            ? values.contact.altLocation
            : undefined,
        },
        assignmentHandling: {
          travel: values.assignmentHandling.travel,
          templateId: values.assignmentHandling.templateId,
          durationInMinutes: isNull(values.assignmentHandling.durationInMinutes)
            ? 0
            : values.assignmentHandling.durationInMinutes,
          priority: values.assignmentHandling.priority,
          severity: values.assignmentHandling.severity,
          methodOfInspection: values.assignmentHandling.methodOfInspection,
          mustEndBefore: endTime,
          mustStartAfter: startTime,
          notes: values.assignmentHandling.notes,
          preferredLanguage: values.assignmentHandling.preferredLanguage,
          assignmentCategory: values.assignmentHandling.assignmentCategory,
          assignmentType: values.assignmentHandling.assignmentType,
          inspectionType: values.assignmentHandling.inspectionType,
        },
        requiredResource: values.requiredResource
          ? {
              name: values.requiredResource.title || '',
              id: values.requiredResource.value.toString() || '',
            }
          : undefined,
        taskType:
          values?.assignmentHandling?.taskType === FNOL_TaskTypes.NON_COMPLEX
            ? undefined
            : values.assignmentHandling?.taskType,
      },
      requiredAttributes:
        values.claimAttributes.length > 0
          ? Object.assign(
              {},
              ...values.claimAttributes.map((value) => ({
                [value.attributeDefinitionIntegrationGUID]:
                  value.selectedAttributeValuesVarchars,
              }))
            )
          : undefined,
    };
  };

  const onIngressRequest = (response: { success: boolean }) => {
    if (response.success) {
      notifySuccess('Success', 'Task Ingressed successfully');
      navigate('/tasks');
      queryClient.invalidateQueries('infos');
    } else {
      notifyError('Error', 'Task Ingressed failed');
    }
  };

  const { ingressRequest, status: ingressStatus } =
    useIngressRequest(onIngressRequest);

  const onHandleIngressRequestFormSubmit = (values: IngressRequestForm) => {
    if (
      formSection === TaskCreate_FormSections.INGRESS ||
      formSection === TaskCreate_FormSections.INITIAL
    ) {
      setSaveButtonLabel('Ingress');
      setFormSection(TaskCreate_FormSections.REVIEW);

      return;
    }

    const request = ingressRequestHelper(values);

    ingressRequest({ request });
  };

  const { company } = useCompany();
  const { companyAttributes } = useGetCompanyAttributeOptions(
    company?.attributeDefinition || []
  );

  return (
    <TaskCreate
      formSection={formSection}
      setFormSection={setFormSection}
      setResourceInput={setResourceInput}
      activeResources={mappedActiveResources}
      fnolStatus={fnolStatus}
      selectedSlot={selectedSlot}
      setSelectedSlot={setSelectedSlot}
      isForced={isForced}
      setIsForced={setIsForced}
      companyAttributes={companyAttributes}
      onHandleFNOLRequestFormSubmit={onHandleFNOLRequestFormSubmit}
      availableSlots={availableSlots}
      onHandleIngressRequestFormSubmit={onHandleIngressRequestFormSubmit}
      ingressStatus={ingressStatus}
      saveButtonLabel={saveButtonLabel}
      setSaveButtonLabel={setSaveButtonLabel}
      timeZone={timeZone}
    />
  );
}
